import { useExpertEditData } from "@/data/use-expert-edit-data";
import { useExpertSkillsData } from "@/data/use-expert-skills-data";
import { useGroupSkillsData } from "@/data/use-group-skills-data";
import { useKitData } from "@/data/use-kits-data";
import { useLibraryFilesData } from "@/data/use-library-files-data";
import { useMessagesData } from "@/data/use-messages-data";
import { useSkillsData } from "@/data/use-skills-data";
import { useGroupAndKitTemplatesData } from "@/data/use-templates-data";
import { isOwnedArtifact } from "@/lib/grant";
import {
  documentToMentionCommand,
  fileToMentionCommand,
  libraryToMentionCommand,
  MentionInfo,
  parseMentions,
  skillToMentionCommand,
  templateToMentionCommand,
} from "@/lib/mentions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { Editor } from "@tiptap/react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useDocumentsData } from "../../data/use-documents-data";
import { useGroupFoldersData } from "../../data/use-group-folder-data";
import { useKitFoldersData } from "../../data/use-kit-folders-data";
import { useGroupUuid } from "../../hooks/use-group-uuid";
import { useKitUuid } from "../../hooks/use-kit-uuid";
import { cn } from "../../lib/utils";
import { useAssistContext } from "../Assist/AssistContext";
import {
  MAX_FILES_COUNT,
  useOptionalChatFileManager,
} from "../file/ChatFileManager/ChatFileManagerContext";
import { TabsDummyButton } from "../ui/tabs";
import { CommandGroupRenderer } from "./CommandGroupRenderer";
import { CommandItemTitle } from "./CommandItemTitle";
import { useCategoryGroups } from "./hooks";
import {
  createNavigationCategories,
  getGroupedItems,
  shouldShowGroups,
} from "./lib";
import { CommandItem } from "./types";

export type CommandCategory =
  | "recently-used"
  | "all-references"
  | "all-files"
  | "all-documents"
  | "all-templates"
  | "all-skills"
  | "all-library";

export interface CommandCategoryConfig {
  key: CommandCategory;
  label: string;
  items: CommandItem[];
  parentKey?: string;
}

interface CommandPanelProps {
  clientRect: () => DOMRect;
  command: (commandProps: CommandItem) => void;
  decorationNode: HTMLElement;
  editor: Editor;
  query: string;
  range: { from: number; to: number };
  ref: (ref: HTMLElement) => void;
  text: string;
  includeSkillMentions?: boolean;
  excludeSkillUuid?: string;
}

export interface CommandPanelRef {
  onKeyDown: (event: KeyboardEvent) => boolean;
}

function SkillMentionItems({
  excludeSkillUuid,
  onItems,
}: {
  excludeSkillUuid?: string;
  onItems: (items: CommandItem[]) => void;
}) {
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const { kit } = useKitData(groupUuid, kitUuid);
  const { expert, expertUuid } = useExpertEditData({ groupUuid, kitUuid });
  const { skillUuids: expertSkillUuids } = useExpertSkillsData(
    groupUuid,
    kitUuid,
    expertUuid,
  );
  const { skills: kitSkills } = useSkillsData(groupUuid, kitUuid);
  const { skills: groupSkills } = useGroupSkillsData({
    groupUuid,
  });

  const items = useMemo(() => {
    const expertSkillUuidSet = new Set(expertSkillUuids);
    const scopePrefixForSkill = (skill: {
      uuid: string;
      readonly?: boolean;
    }): string | null => {
      if (expertSkillUuidSet.has(skill.uuid) && expert?.name) {
        return expert.name;
      }
      if (skill.readonly === false && kit?.name) {
        return kit.name;
      }
      return null;
    };

    const kitSkillUuids = new Set(kitSkills.map((skill) => skill.uuid));
    const kitItems = kitSkills
      .filter((skill) => skill.uuid !== excludeSkillUuid)
      .map((skill) =>
        skillToMentionCommand({
          skill: {
            ...skill,
            scopePrefix: scopePrefixForSkill(skill),
          },
        }),
      );
    const groupItems = groupSkills
      .filter(
        (skill) =>
          skill.uuid !== excludeSkillUuid && !kitSkillUuids.has(skill.uuid),
      )
      .map((skill) =>
        skillToMentionCommand({
          skill: {
            ...skill,
            scopePrefix: scopePrefixForSkill(skill),
          },
        }),
      );

    return [...kitItems, ...groupItems];
  }, [
    kitSkills,
    groupSkills,
    excludeSkillUuid,
    expert?.name,
    expertSkillUuids,
    kit?.name,
  ]);

  useEffect(() => {
    onItems(items);
  }, [items, onItems]);

  return null;
}

export const CommandPanel = forwardRef<CommandPanelRef, CommandPanelProps>(
  (props, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [selectedCategory, setSelectedCategory] =
      useState<CommandCategory>("all-references");
    const [expandedGroups, setExpandedGroups] = useState<
      Record<string, boolean>
    >({});
    const [skillItems, setSkillItems] = useState<CommandItem[]>([]);

    // Load data from hooks
    const groupUuid = useGroupUuid();
    const kitUuid = useKitUuid();
    const { activeChatUuid } = useAssistContext();
    const { messages } = useMessagesData(groupUuid, kitUuid, activeChatUuid);

    const { folders, files: kitFiles } = useKitFoldersData(groupUuid, kitUuid);
    const { linkFolder, linkTemplate, kit } = useKitData(groupUuid, kitUuid);
    const listAccess = kit?.type === "collection" ? "owner" : undefined;
    const { folders: groupFolders, files: groupFiles } = useGroupFoldersData({
      groupUuid,
      access: listAccess,
    });
    const { documents } = useDocumentsData(groupUuid, kitUuid);
    const { kitTemplates, remainingGroupTemplates } =
      useGroupAndKitTemplatesData({
        groupUuid,
        kitUuid,
        access: listAccess,
      });
    const { libraryFiles } = useLibraryFilesData(groupUuid, kitUuid);
    const chatFileManager = useOptionalChatFileManager();

    const recentMentionIds = useMemo(() => {
      const mentionIds = new Map<MentionInfo["id"], MentionInfo>();

      [...messages]
        .filter((m) => m.type === "user")
        .reverse()
        .forEach((message) => {
          const mentions = parseMentions(message.content);
          mentions.forEach((mention: MentionInfo) => {
            mentionIds.set(mention.id, mention);
            if (mentionIds.size >= 10) return;
          });
        });

      return mentionIds;
    }, [messages]);

    const kitFileUuids = useMemo(
      () => new Set(kitFiles.map((file) => file.uuid)),
      [kitFiles],
    );

    const kitFileItems: CommandItem[] = useMemo(
      () =>
        kitFiles.map((file) => {
          const folder = folders.find((item) => item.uuid === file.folderUuid);
          let scopePrefix: string | null = file.groupName ?? null;
          if (folder && isOwnedArtifact(folder) && kit?.name) {
            scopePrefix = kit.name;
          }
          return fileToMentionCommand({ file: { ...file, scopePrefix } });
        }),
      [kitFiles, folders, kit?.name],
    );

    const groupFileItems: CommandItem[] = useMemo(
      () =>
        groupFiles
          .filter((file) => !kitFileUuids.has(file.uuid))
          .map((file) => fileToMentionCommand({ file })),
      [groupFiles, kitFileUuids],
    );

    const textDocumentItems: CommandItem[] = documents.map((d) =>
      documentToMentionCommand({ document: d }),
    );

    const kitTemplateItems: CommandItem[] = kitTemplates.map((t) =>
      templateToMentionCommand({ template: t }),
    );

    const groupTemplateItems: CommandItem[] = remainingGroupTemplates.map((t) =>
      templateToMentionCommand({ template: t }),
    );

    const libraryItems: CommandItem[] = libraryFiles.map((f) =>
      libraryToMentionCommand({ file: f }),
    );

    const { rootCategories, recentItems } = useCategoryGroups({
      folders,
      groupFolders: groupFolders,
      briefDocumentItems: [],
      textDocumentItems,
      sectionItems: [],
      kitFileItems,
      groupFileItems,
      kitTemplateItems,
      groupTemplateItems,
      libraryItems,
      skillItems,
      recentMentionIds,
      query: props.query,
    });

    const allNavigationCategories = createNavigationCategories(rootCategories);

    const groupedItems = getGroupedItems(
      selectedCategory,
      kitFileItems,
      groupFileItems,
      [], // briefDocumentItems
      textDocumentItems,
      [], // sectionItems,
      kitTemplateItems,
      groupTemplateItems,
      libraryItems,
      recentItems,
      props.query,
      skillItems,
    );

    const currentCategory = allNavigationCategories.find(
      (c) => c.key === selectedCategory,
    );
    const currentItems = currentCategory?.items || [];

    const getFlattenedItems = () => {
      if (!shouldShowGroups(selectedCategory)) {
        return currentItems;
      }

      const flatItems: (
        | CommandItem
        | { type: "show-more"; groupTitle: string; count: number }
      )[] = [];

      groupedItems.forEach((group) => {
        if (group.items.length === 0) return;

        const isExpanded = expandedGroups[group.title];
        const itemsToShow = isExpanded ? group.items : group.items.slice(0, 4);

        flatItems.push(...itemsToShow);

        if (!isExpanded && group.items.length > 4) {
          flatItems.push({
            type: "show-more",
            groupTitle: group.title,
            count: group.items.length - 4,
          });
        }
      });

      return flatItems;
    };

    const flattenedItems = getFlattenedItems();
    const totalItems = flattenedItems.length;

    const selectItem = async (index: number) => {
      if (index < 0 || index >= flattenedItems.length) return;

      const item = flattenedItems[index];

      if ("type" in item && item.type === "show-more") {
        setExpandedGroups((prev) => ({
          ...prev,
          [item.groupTitle]: true,
        }));

        const newFlattenedItems = getFlattenedItems();
        const newTotalItems = newFlattenedItems.length;

        setSelectedIndex(Math.min(index, newTotalItems - 1));
        return;
      }

      if ("id" in item) {
        if (item.type === "library") {
          const libraryFile = libraryFiles.find(
            (f) => f.uuid === item.artifactUuid,
          );

          if (libraryFile && chatFileManager) {
            const isAlreadyAttached =
              chatFileManager.chatFiles.some(
                (f) => f.uuid === item.artifactUuid,
              ) ||
              chatFileManager.selectedLibraryFiles.some(
                (f) => f.uuid === item.artifactUuid,
              );

            if (
              !isAlreadyAttached &&
              chatFileManager.filesCount >= MAX_FILES_COUNT
            ) {
              chatFileManager.addLibraryFile({
                uuid: libraryFile.uuid,
                name: libraryFile.name,
                mimeType: libraryFile.mimeType,
              });
              return;
            }

            if (!isAlreadyAttached) {
              await chatFileManager.addLibraryFile({
                uuid: libraryFile.uuid,
                name: libraryFile.name,
                mimeType: libraryFile.mimeType,
              });
            }
          }

          props.command({ ...item });
          return;
        }

        // Link artifacts
        if (item.type === "template") {
          if (
            groupTemplateItems.find((t) => t.artifactUuid === item.artifactUuid)
          ) {
            linkTemplate({
              templateUuid: item.artifactUuid,
            });
          }
        }

        if (item.type === "file") {
          const file = groupFiles.find((f) => f.uuid === item.artifactUuid);

          if (file) {
            linkFolder({
              folderUuid: file.folderUuid,
            });
          }
        }

        props.command({ ...item });
      }
    };

    useEffect(() => {
      setSelectedIndex(0);
    }, [props.query, selectedCategory]);

    useEffect(() => {
      return () => {
        setExpandedGroups({});
      };
    }, []);

    useImperativeHandle(ref, () => ({
      onKeyDown: (event: KeyboardEvent) => {
        if (totalItems === 0) return false;

        if (event.key === "ArrowUp") {
          setSelectedIndex((selectedIndex + totalItems - 1) % totalItems);
          return true;
        }

        if (event.key === "ArrowDown") {
          setSelectedIndex((selectedIndex + 1) % totalItems);
          return true;
        }

        if (event.key === "Enter") {
          selectItem(selectedIndex);
          return true;
        }

        if (event.key === "Tab") {
          event.preventDefault();
          if (allNavigationCategories.length === 0) return false;

          const currentIndex = allNavigationCategories.findIndex(
            (c) => c.key === selectedCategory,
          );
          const nextIndex = event.shiftKey
            ? (currentIndex - 1 + allNavigationCategories.length) %
              allNavigationCategories.length
            : (currentIndex + 1) % allNavigationCategories.length;

          setSelectedCategory(allNavigationCategories[nextIndex].key);
          setSelectedIndex(0);
          return true;
        }

        return false;
      },
    }));

    const hasResults = allNavigationCategories.some((c) => c.items.length > 0);

    return (
      <div className="flex max-w-[330px] flex-col gap-2 p-1">
        {props.includeSkillMentions && (
          <SkillMentionItems
            excludeSkillUuid={props.excludeSkillUuid}
            onItems={setSkillItems}
          />
        )}
        {hasResults ? (
          <>
            <div className="border-border flex flex-wrap gap-1 border-b pb-1">
              {rootCategories.map((category) => (
                <TabsDummyButton
                  key={category.key}
                  isActive={
                    selectedCategory === category.key ||
                    (selectedCategory.startsWith("folder:") &&
                      category.key === "all-files") ||
                    (selectedCategory.startsWith("documents:") &&
                      category.key === "all-documents") ||
                    (selectedCategory === "recently-used" &&
                      category.key === "recently-used")
                  }
                  onMouseUp={() => {
                    setSelectedCategory(category.key);
                    setSelectedIndex(0);
                  }}
                  className="h-6 px-2 py-1 text-xs whitespace-nowrap"
                >
                  {category.label}
                </TabsDummyButton>
              ))}
            </div>

            <div className="max-h-[300px] overflow-y-auto">
              {shouldShowGroups(selectedCategory) ? (
                <CommandGroupRenderer
                  groups={groupedItems}
                  selectedIndex={selectedIndex}
                  onSelectItem={selectItem}
                  expandedGroups={expandedGroups}
                  onToggleExpand={(groupTitle: string) => {
                    setExpandedGroups((prev) => ({
                      ...prev,
                      [groupTitle]: !prev[groupTitle],
                    }));
                  }}
                />
              ) : (
                currentItems.map((item, index) => (
                  <button
                    key={item.label}
                    className={cn(
                      "min-h-[32px] w-full rounded px-2 py-1 text-left text-sm break-words",
                      selectedIndex === index && "bg-background-10",
                    )}
                    onClick={() => selectItem(index)}
                    title={item.unavailable ?? item.label}
                  >
                    <CommandItemTitle
                      label={item.label}
                      unavailable={item.unavailable}
                    />
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="font-family-fancy px-2 py-1 text-sm text-gray-500">
            <FontAwesomeIcon icon={faBan} /> No results
          </div>
        )}
      </div>
    );
  },
);

CommandPanel.displayName = "Command";
